// App Config editor: view/edit an app's bundled config.json (App Config feature).
//
// Loads GET /apps/<app>/config → { config, schema, canEdit }. Renders one field per key the app
// declares in package.json `atlas.config`; owners (canEdit) can edit + Save, which writes back to
// the live bundle's config.json (no redeploy). Non-owners see read-only values.
//
// Two entry points share the same editor via <ConfigEditor appName=…>:
//   - the standalone "Config" tab (<Config>), which takes an app-name input, and
//   - the app detail page (AppDetail), which passes the already-selected app.
import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SaveIcon from "@mui/icons-material/Save";
import SearchIcon from "@mui/icons-material/Search";

import TuneIcon from "@mui/icons-material/Tune";

import type { AppConfigPayload, Client } from "../types";
import { MONO } from "../theme";
import { EmptyState, PageHeader } from "../components/ui";

// The privacy warning shown above an editable config (config is authed/private, not
// a public asset; the design board's "public /config.json" text is stale and intentionally not used).
function ConfigWarning() {
  return (
    <Alert severity="warning">
      <AlertTitle sx={{ fontWeight: 600 }}>
        Readable by any signed-in Atlas user. Never store secrets here.
      </AlertTitle>
      The app reads these values in the browser, so they are visible to that user. Use it for
      endpoints, flags, and region names. Broker API keys, tokens, and passwords through a backend
      service instead.
    </Alert>
  );
}

// The reusable editor. Given an appName, it loads + renders the config for that app. Re-loads when
// appName changes. Renders nothing until an appName is provided (the tab's initial empty state).
// `variant` tunes the two board layouts that share this editor: the standalone Config screen (value
// column 320, footer buttons justified to the end, no hint) vs. the AppDetail Configuration section
// (value column 380, footer with the "saving is live" hint on the left).
export function ConfigEditor({
  client,
  appName,
  variant = "standalone",
}: {
  client: Client;
  appName: string;
  variant?: "standalone" | "detail";
}) {
  const valueColWidth = variant === "detail" ? 380 : 320;
  const [loaded, setLoaded] = useState<AppConfigPayload | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({}); // key -> string being edited
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  function seedDraft(res: AppConfigPayload) {
    const seed: Record<string, string> = {};
    for (const key of Object.keys(res.schema || {})) {
      const val = res.config?.[key];
      seed[key] = val === undefined ? "" : JSON.stringify(val);
    }
    setDraft(seed);
  }

  useEffect(() => {
    if (!appName) {
      setLoaded(null);
      return;
    }
    let active = true;
    setError(null);
    setSaved(false);
    client
      .getAppConfig(appName)
      .then((res) => {
        if (!active) return;
        setLoaded(res);
        seedDraft(res); // seed the draft from current values (stringified so any JSON type is editable)
      })
      .catch((err) => {
        if (!active) return;
        setLoaded(null);
        setError((err as Error).message);
      });
    return () => {
      active = false;
    };
  }, [client, appName]);

  async function save(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSaved(false);
    try {
      // Each field is JSON (so booleans/numbers/arrays work); fall back to a raw string.
      const config: Record<string, unknown> = {};
      for (const [key, raw] of Object.entries(draft)) {
        if (raw === "") continue; // omit empty fields
        try {
          config[key] = JSON.parse(raw);
        } catch {
          config[key] = raw;
        }
      }
      await client.saveAppConfig(appName, config);
      setSaved(true);
      const res = await client.getAppConfig(appName); // reload to reflect the persisted values
      setLoaded(res);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  if (!appName) return null;

  const schema = loaded?.schema || {};
  const keys = Object.keys(schema);
  const canEdit = !!loaded?.canEdit;

  return (
    <Stack spacing={2}>
      {error && <Alert severity="error">{error}</Alert>}
      {saved && <Alert severity="success">Saved. Changes are live now; a redeploy will overwrite them.</Alert>}

      {loaded === null && !error && (
        <Paper variant="outlined" sx={{ p: 2.5 }} aria-label="Loading app config">
          <Stack spacing={2}>
            {[0, 1, 2].map((row) => (
              <Stack
                key={row}
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems={{ xs: "stretch", sm: "center" }}
              >
                <Skeleton width={140} height={24} />
                <Skeleton sx={{ flexGrow: 1 }} height={24} />
                <Skeleton width={valueColWidth} height={42} />
              </Stack>
            ))}
          </Stack>
        </Paper>
      )}

      {loaded !== null && (
        <>
          {!canEdit && (
            <Typography color="text.secondary">
              Read-only. Only the app&apos;s owning team can edit its config.
            </Typography>
          )}
          {canEdit && keys.length > 0 && <ConfigWarning />}

          {keys.length === 0 ? (
            <Typography color="text.secondary">
              This app declares no config keys (package.json `atlas.config`).
            </Typography>
          ) : (
            <Paper variant="outlined" component="form" onSubmit={save} sx={{ overflow: "hidden" }}>
              <TableContainer>
                <Table sx={{ minWidth: 720 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: 220 }}>Key</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell sx={{ width: valueColWidth }}>Value</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {keys.map((key) => (
                      // Config rows use [16,20] padding per the board (the theme default is [12,20]).
                      <TableRow key={key} sx={{ "&:last-child td": { border: 0 }, "& td": { py: 2 } }}>
                        <TableCell sx={{ fontFamily: MONO, fontWeight: 500 }}>{key}</TableCell>
                        <TableCell sx={{ color: "text.secondary" }}>
                          {schema[key]?.description || ""}
                        </TableCell>
                        <TableCell>
                          {canEdit ? (
                            <TextField
                              fullWidth
                              value={draft[key] ?? ""}
                              onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
                              slotProps={{ input: { sx: { fontFamily: MONO, fontSize: 13 } } }}
                            />
                          ) : (
                            <Box component="code" sx={{ fontFamily: MONO }}>
                              {JSON.stringify(loaded.config?.[key])}
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {canEdit && (
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1.5}
                  justifyContent={variant === "detail" ? "space-between" : "flex-end"}
                  alignItems={{ xs: "stretch", sm: "center" }}
                  sx={{ p: 2, borderTop: 1, borderColor: "divider" }}
                >
                  {variant === "detail" && (
                    <Typography variant="body2" color="text.secondary">
                      Saving writes to the live bundle and takes effect with no redeploy.
                    </Typography>
                  )}
                  <Stack direction="row" spacing={1.25} justifyContent="flex-end">
                    <Button type="button" variant="text" startIcon={<RestartAltIcon />} onClick={() => loaded && seedDraft(loaded)}>
                      Reset
                    </Button>
                    <Button type="submit" variant="contained" startIcon={<SaveIcon />}>
                      {variant === "detail" ? "Save changes" : "Save config"}
                    </Button>
                  </Stack>
                </Stack>
              )}
            </Paper>
          )}
        </>
      )}
    </Stack>
  );
}

// The standalone "Config" tab: an app-name input that drives the shared ConfigEditor.
export function Config({ client }: { client: Client }) {
  const [appName, setAppName] = useState("");
  const [loadName, setLoadName] = useState(""); // the app the editor is currently showing

  return (
    <Stack spacing={3.5}>
      <PageHeader
        title="App Config"
        subtitle="Edit an app's bundled config.json live without redeploying. Owners can save changes."
      />
      <Stack
        component="form"
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        alignItems={{ xs: "stretch", sm: "flex-end" }}
        onSubmit={(e) => {
          e.preventDefault();
          setLoadName(appName);
        }}
      >
        <TextField
          label="App name"
          placeholder="e.g. orion"
          value={appName}
          onChange={(e) => setAppName(e.target.value)}
          sx={{ width: { xs: "100%", sm: 280 } }}
        />
        <Button type="submit" variant="contained" startIcon={<SearchIcon />}>
          Load config
        </Button>
      </Stack>
      {!loadName && (
        <EmptyState
          icon={<TuneIcon />}
          title="Load an app's config"
          hint="Enter an app name above to view its config. The owning team can edit values without redeploying."
          dense
        />
      )}
      <ConfigEditor client={client} appName={loadName} />
    </Stack>
  );
}
